import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { label: "Services", id: "services", testid: "nav-link-services" },
  { label: "Approach", id: "approach", testid: "nav-link-approach" },
  { label: "Why AiCendor", id: "why", testid: "nav-link-why" },
  { label: "Insights", id: "insights", testid: "nav-link-insights" },
  { label: "Contact", id: "contact", testid: "nav-link-contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled || open
          ? "border-b border-white/10 bg-ink/80 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <button
          data-testid="nav-logo-home"
          onClick={() => (window.__lenis ? window.__lenis.scrollTo(0, { duration: 1.2 }) : window.scrollTo({ top: 0, behavior: "smooth" }))}
          aria-label="AiCendor home"
        >
          <Logo />
        </button>
        <nav className="hidden items-center gap-9 md:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => go(l.id)}
              className="link-underline font-mono text-[11px] uppercase tracking-[0.22em] text-slate-300 transition-colors hover:text-white"
            >
              {l.label}
            </button>
          ))}
        </nav>
        <button
          data-testid="nav-cta-button"
          onClick={() => go("book")}
          className="btn-sweep group hidden items-center gap-2 border border-white/25 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.22em] text-white md:flex"
        >
          Book a call
          <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </button>
        <button
          data-testid="nav-mobile-toggle"
          className="text-white md:hidden"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div className="border-t border-white/10 bg-ink/95 backdrop-blur-xl md:hidden">
          <div className="flex flex-col px-6 pb-6 pt-2">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`${l.testid}-mobile`}
                onClick={() => go(l.id)}
                className="py-3.5 text-left font-mono text-xs uppercase tracking-[0.22em] text-slate-200"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-cta-button-mobile"
              onClick={() => go("book")}
              className="btn-sweep mt-4 border border-white/25 px-5 py-3.5 font-mono text-xs uppercase tracking-[0.22em] text-white"
            >
              Book a call
            </button>
          </div>
        </div>
      )}
    </motion.header>
  );
}
